// ABOUTME: Pure helpers for picking "primary" and "scheduled" subscriptions from a list.
// ABOUTME: Extracted from useSubscriptions so the picking logic can be unit-tested.

import { SubscriptionStatus } from "@/graphql/main/gql/graphql"

interface SubscriptionLike {
  status: SubscriptionStatus
  currentPeriodStart: string
}

const latestByStart = <T extends SubscriptionLike>(subs: T[]): T =>
  subs.reduce((best, s) =>
    new Date(s.currentPeriodStart).getTime() > new Date(best.currentPeriodStart).getTime() ? s : best,
  )

// Pick the subscription that drives the card's main display. Live rows
// (ACTIVE / TRIALING / PAST_DUE) win so a SCHEDULED row created by an
// AFTER_CURRENT plan change — or by a trial-conversion change — does not
// hijack the card and hide the still-running period. If no live row exists
// (lapsed user) we fall back to the newest by currentPeriodStart so the
// cancelled/expired history still surfaces. Multiple rows tied on the same
// latest start date are all returned — callers expect an array.
export const pickPrimarySubscriptions = <T extends SubscriptionLike>(subscriptions: T[]): T[] => {
  if (subscriptions.length === 0) return []
  const live = subscriptions.filter((s) => isLiveSubscription(s.status))
  const pool = live.length > 0 ? live : subscriptions
  const latestTime = new Date(latestByStart(pool).currentPeriodStart).getTime()
  return pool.filter((s) => new Date(s.currentPeriodStart).getTime() === latestTime)
}

// Statuses under which a subscription is "live": paid, in trial, or in the
// payment-failure grace period. CANCELLED technically keeps access until the
// period end, but product-wise it belongs to history — e.g. the coach's
// athlete-PRO list shows only live rows, so a cancelled subscription
// disappears from it immediately.
const LIVE_STATUSES: readonly SubscriptionStatus[] = [
  SubscriptionStatus.Active,
  SubscriptionStatus.Trialing,
  SubscriptionStatus.PastDue,
]

export const isLiveSubscription = (status: SubscriptionStatus): boolean => LIVE_STATUSES.includes(status)

// Pick the SCHEDULED row produced by an AFTER_CURRENT plan change so the card
// can render the "switching on DD.MM.YYYY" banner. Returns null if there is
// none. If multiple SCHEDULED rows exist (shouldn't in normal flow) we keep
// the newest — the others are stale leftovers.
export const pickScheduledSubscription = <T extends SubscriptionLike>(subscriptions: T[]): T | null => {
  const scheduled = subscriptions.filter((s) => s.status === SubscriptionStatus.Scheduled)
  if (scheduled.length === 0) return null
  return latestByStart(scheduled)
}
